import { useCallback, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  updateEstablishmentsMutationAtom,
  deleteEstablishmentsMutationAtom,
  buildEstablishmentsUrl,
  mergeEstablishmentsAtom,
  removeEstablishmentsAtom,
} from 'atoms/establishments-atoms';
import { resourcesAtom } from 'atoms/root';
import { appendQueryParams } from 'lib/ui';
import type { EstablishmentPayload } from 'types/api-payloads';

export interface MutationResult {
  success: boolean;
  errors: string[] | null;
  data?: any; // Response data from server
}

export const useEstablishments = () => {
  const [error, setError] = useState<string | null>(null);
  const resources = useAtomValue(resourcesAtom);
  const baseHref = resources?.establishments?.href;

  const updateMutation = useSetAtom(updateEstablishmentsMutationAtom);
  const deleteMutation = useSetAtom(deleteEstablishmentsMutationAtom);
  const mergeEstablishments = useSetAtom(mergeEstablishmentsAtom);
  const removeEstablishments = useSetAtom(removeEstablishmentsAtom);

  const updateEstablishments = useCallback(
    (
      referenceLocationId: number,
      establishmentsPayload: EstablishmentPayload[]
    ): Promise<MutationResult> => {
      if (!baseHref) {
        return Promise.resolve({
          success: false,
          errors: ['Establishments resource not available'],
        });
      }

      setError(null);

      return new Promise((resolve) => {
        const url = buildEstablishmentsUrl(baseHref, referenceLocationId);

        updateMutation({
          url,
          type: 'PATCH',
          data: {
            establishments: establishmentsPayload,
            reference_location_id: referenceLocationId,
          },
          onSuccess: (responseData) => {
            if (responseData) {
              const establishments = Array.isArray(responseData)
                ? responseData
                : responseData.establishments;
              if (Array.isArray(establishments)) {
                mergeEstablishments(establishments);
              }
            }
            resolve({ success: true, errors: null, data: responseData });
          },
          onError: (errorData) => {
            const errors = errorData?.errors ?? [
              'Failed to update establishments',
            ];
            setError(errors.join(', '));
            resolve({ success: false, errors });
          },
        });
      });
    },
    [updateMutation, baseHref, mergeEstablishments]
  );

  const deleteEstablishments = useCallback(
    (
      referenceLocationId: number,
      establishmentDate?: string,
      entityId?: number
    ): Promise<MutationResult> => {
      if (!baseHref) {
        return Promise.resolve({
          success: false,
          errors: ['Establishments resource not available'],
        });
      }

      setError(null);

      return new Promise((resolve) => {
        const url = appendQueryParams(
          buildEstablishmentsUrl(baseHref, referenceLocationId),
          {
            establishment_date: establishmentDate,
            entity_id: entityId,
          }
        );

        deleteMutation({
          url,
          type: 'DELETE',
          data: {},
          onSuccess: (responseData) => {
            removeEstablishments({
              referenceLocationId,
              establishmentDate,
              entityId,
            });
            resolve({ success: true, errors: null, data: responseData });
          },
          onError: (errorData) => {
            const errors = errorData?.errors ?? [
              'Failed to delete establishments',
            ];
            setError(errors.join(', '));
            resolve({ success: false, errors });
          },
        });
      });
    },
    [deleteMutation, baseHref, removeEstablishments]
  );

  return {
    error,
    updateEstablishments,
    deleteEstablishments,
  };
};
